import { type FC, useMemo, useState } from "react";

import { type ClipboardStatus, useClipboard } from "../../hooks/useClipboard";
import { Alert } from "./Alert";
import { Button } from "./Button";

function resolveCopyLabel(status: ClipboardStatus): string {
  switch (status) {
    case "copied": {
      return "Copied!";
    }
    case "failed": {
      return "Failed";
    }
    case "idle": {
      return "Copy";
    }
  }
}

function resolveCopyStatusClassName(status: ClipboardStatus): string {
  switch (status) {
    case "failed": {
      return "text-rose-600 hover:text-rose-700 dark:text-rose-200 dark:hover:text-rose-100";
    }
    case "copied": {
      return "text-emerald-600 hover:text-emerald-700 dark:text-emerald-200 dark:hover:text-emerald-100";
    }
    case "idle": {
      return "";
    }
  }
}

export type JsonViewerMode = "pretty" | "raw";

export interface JsonViewerProps {
  data: unknown;
  className?: string;
  /**
   * Tailwind max-height class applied to the scroll container.
   * Example: "max-h-64", "max-h-72".
   */
  maxHeight?: string;
  /**
   * Whether to show the copy button (copies the rendered JSON string).
   */
  showCopyButton?: boolean;
  /**
   * Optional container class overrides for background, padding, and border-radius.
   * If not provided, default styling is applied.
   */
  containerClassName?: string;
  defaultMode?: JsonViewerMode;
  showToggle?: boolean;
  prettyIndent?: number;
}

/**
 * JsonViewer Component
 *
 * Displays JSON-like data in a formatted, scrollable container with an optional copy button.
 *
 * Notes / behavior:
 * - Accepts `unknown` because callers often pass untyped metadata blobs.
 * - Uses a safe stringify that tolerates `undefined`, BigInt, Error, Map/Set, and circular refs.
 */
export const JsonViewer: FC<JsonViewerProps> = ({
  data,
  className = "",
  maxHeight = "max-h-64",
  showCopyButton = true,
  containerClassName,
  defaultMode,
  showToggle = true,
  prettyIndent = 2,
}) => {
  const { status: copyStatus, copy } = useClipboard();
  const [mode, setMode] = useState<JsonViewerMode>(() => {
    if (defaultMode) {
      return defaultMode;
    }
    return typeof data === "string" ? "raw" : "pretty";
  });

  const { prettyText, rawText, parseError } = useMemo(() => {
    if (typeof data === "string") {
      const text = data;
      const trimmed = text.trim();
      if (trimmed.length === 0) {
        return {
          parseError: null as string | null,
          prettyText: "",
          rawText: text,
        };
      }

      try {
        const parsed = JSON.parse(trimmed) as unknown;
        return {
          parseError: null as string | null,
          prettyText: safeStringifyJson(parsed, prettyIndent),
          rawText: text,
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : "Invalid JSON";
        return { parseError: message, prettyText: text, rawText: text };
      }
    }

    return {
      parseError: null as string | null,
      prettyText: safeStringifyJson(data, prettyIndent),
      rawText: safeStringifyJson(data),
    };
  }, [data, prettyIndent]);

  const displayedText = mode === "pretty" ? prettyText : rawText;

  const handleCopy = () => {
    void copy(displayedText);
  };

  // Default container styling if not overridden
  const defaultContainerClass =
    "relative rounded-none border border-slate-300 bg-gray-50 p-3 text-gray-900 dark:border-slate-700 dark:bg-gray-900/90 dark:text-white";
  const finalContainerClass = containerClassName ?? defaultContainerClass;
  const hasControls = showToggle || showCopyButton;
  const headerRightPaddingClass = hasControls ? "pr-32" : "";
  const contentPaddingClass = hasControls ? "pr-32 pt-10" : "";

  const copyLabel = resolveCopyLabel(copyStatus);
  const copyStatusClassName = resolveCopyStatusClassName(copyStatus);

  return (
    <div className={finalContainerClass}>
      <div className="absolute right-2 top-2 flex items-center gap-1">
        {showToggle && (
          <div
            className="rounded-none border border-slate-300 bg-gray-200/70 p-0.5 dark:border-slate-700 dark:bg-white/10"
            role="group"
            aria-label="View mode"
          >
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setMode("pretty")}
              className={
                mode === "pretty"
                  ? "bg-gray-200 text-gray-900 dark:bg-white/20 dark:text-white"
                  : ""
              }
              aria-pressed={mode === "pretty"}
              title="Pretty"
            >
              Pretty
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setMode("raw")}
              className={
                mode === "raw"
                  ? "bg-gray-200 text-gray-900 dark:bg-white/20 dark:text-white"
                  : ""
              }
              aria-pressed={mode === "raw"}
              title="Raw"
            >
              Raw
            </Button>
          </div>
        )}

        {showCopyButton && (
          <Button
            type="button"
            onClick={handleCopy}
            className={copyStatusClassName}
            title="Copy"
            variant="ghost"
            size="sm"
          >
            {copyLabel}
          </Button>
        )}
      </div>

      {parseError && mode === "pretty" && (
        <Alert
          variant="warning"
          size="sm"
          className={`mb-2 ${headerRightPaddingClass}`}
        >
          Invalid JSON string: {parseError}
        </Alert>
      )}
      <pre
        className={`overflow-auto text-xs text-gray-800 dark:text-white/80 ${maxHeight} scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300 dark:scrollbar-thumb-white/20 ${contentPaddingClass} ${className}`}
      >
        {displayedText}
      </pre>
    </div>
  );
};

function safeStringifyJson(value: unknown, space?: number): string {
  if (typeof value === "string") {
    return value;
  }

  const seen = new WeakSet<object>();
  const replacer = (_key: string, nextValue: unknown) => {
    if (typeof nextValue === "bigint") {
      return nextValue.toString();
    }
    if (nextValue instanceof Error) {
      return {
        message: nextValue.message,
        name: nextValue.name,
        stack: nextValue.stack,
      };
    }
    if (nextValue instanceof Map) {
      return Array.from(nextValue.entries()) as [unknown, unknown][];
    }
    if (nextValue instanceof Set) {
      return Array.from(nextValue.values()) as unknown[];
    }
    if (typeof nextValue === "object" && nextValue !== null) {
      if (seen.has(nextValue)) {
        return "[Circular]";
      }
      seen.add(nextValue);
    }
    return nextValue;
  };

  try {
    const result = JSON.stringify(value, replacer, space) as string | undefined;
    if (result === undefined) {
      return String(value);
    }
    return result;
  } catch {
    return String(value);
  }
}
